import Wordmark from "../components/Wordmark";

const LAST_UPDATED = "7 April 2026";
const COMPANY = "Bethany Labs";
const CONTACT_EMAIL = "hello@bethanylabs.com";
const PRODUCT = "YouSift";

function Section({ title, children }) {
  return (
    <div className="mb-10">
      <h2 className="text-xl font-bold text-white mb-4">{title}</h2>
      <div className="space-y-4 text-gray-400 leading-relaxed">{children}</div>
    </div>
  );
}

function Badge({ label, status }) {
  const colours = {
    compliant: "bg-green-500/10 text-green-400 border-green-500/20",
    partial: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    pending: "bg-gray-500/10 text-gray-400 border-gray-500/20",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${colours[status]}`}>
      {label}
    </span>
  );
}

export default function DataCompliance() {
  return (
    <div className="min-h-screen bg-[#0F0F0F] flex flex-col">
      <header className="px-6 py-5 border-b border-[#1A1A1A] flex items-center justify-between">
        <a href="/"><Wordmark size="sm" darkBg={true} /></a>
        <a href="/" className="text-gray-400 hover:text-white text-sm transition-colors">← Back</a>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-6 py-16">
        <div className="mb-12">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-500">Legal</span>
          <h1 className="text-4xl font-black text-white mt-3 mb-2">Data & Compliance</h1>
          <p className="text-gray-500 text-sm">Last updated: {LAST_UPDATED}</p>
        </div>

        {/* Compliance status overview */}
        <div className="bg-[#111] border border-[#2A2A2A] rounded-2xl p-6 mb-12">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-4">Compliance Status</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: "UK GDPR", status: "compliant" },
              { label: "Stripe PCI DSS", status: "compliant" },
              { label: "ISO 27001", status: "pending" },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-4 py-3">
                <span className="text-white text-sm font-medium">{item.label}</span>
                <Badge label={item.status === "compliant" ? "Compliant" : item.status === "partial" ? "Partial" : "In Progress"} status={item.status} />
              </div>
            ))}
          </div>
        </div>

        <Section title="1. Overview">
          <p>
            {COMPANY} is committed to responsible data handling. This page describes how {PRODUCT} collects, processes, stores, and protects data — and our compliance obligations under applicable law.
          </p>
          <p>
            {PRODUCT} is operated by {COMPANY}, a UK-based company. Our primary legal obligation is compliance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
          </p>
        </Section>

        <Section title="2. Data We Process">
          <div className="space-y-3">
            {[
              {
                category: "Usage Data",
                description: "Anonymous user identifiers, sift count, and timestamps. Not linked to any personally identifiable information.",
                basis: "Legitimate interest",
              },
              {
                category: "Video Content",
                description: "YouTube URLs and the transcripts derived from them. Used only to generate content on your behalf and not retained longer than necessary.",
                basis: "Contract performance",
              },
              {
                category: "Quiz Responses",
                description: "Answers to the onboarding quiz, stored against your anonymous identifier. Used only to personalise your experience.",
                basis: "Legitimate interest",
              },
              {
                category: "Payment Data",
                description: "Processed entirely by Stripe. We receive only a Stripe customer ID and subscription status — never card details.",
                basis: "Contract performance",
              },
            ].map((item) => (
              <div key={item.category} className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-5 py-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-white font-semibold text-sm">{item.category}</p>
                  <span className="text-xs text-gray-500 bg-[#2A2A2A] px-2 py-0.5 rounded-full">{item.basis}</span>
                </div>
                <p className="text-gray-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section title="3. Data Storage and Security">
          <p>
            All data is stored in MongoDB Atlas, hosted on infrastructure within the European Economic Area (EEA). Data in transit is encrypted via TLS 1.2+. Data at rest is encrypted using AES-256.
          </p>
          <p>
            Our API is hosted on Railway with HTTPS enforced. The frontend is served via Vercel's global CDN with automatic HTTPS.
          </p>
          <p>
            Access to production data is restricted to authorised {COMPANY} personnel only. We do not share raw user data with any third party except where required to deliver the service (see Third-Party Processors below).
          </p>
        </Section>

        <Section title="4. Third-Party Processors">
          <p>
            We use the following sub-processors to deliver {PRODUCT}. All are bound by appropriate data processing agreements.
          </p>
          <div className="mt-4 space-y-3">
            {[
              { name: "Anthropic", location: "USA", purpose: "AI content generation", dpa: true },
              { name: "Supadata", location: "USA", purpose: "YouTube transcript retrieval", dpa: true },
              { name: "AssemblyAI", location: "USA", purpose: "Fallback transcription", dpa: true },
              { name: "MongoDB Atlas", location: "EEA", purpose: "Data storage", dpa: true },
              { name: "Stripe", location: "USA/EEA", purpose: "Payment processing", dpa: true },
              { name: "Vercel", location: "USA/EEA", purpose: "Frontend hosting", dpa: true },
              { name: "Railway", location: "USA", purpose: "Backend hosting", dpa: true },
            ].map((p) => (
              <div key={p.name} className="grid grid-cols-3 gap-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-4 py-3 text-sm">
                <span className="text-white font-medium">{p.name}</span>
                <span className="text-gray-500">{p.location}</span>
                <span className="text-gray-400">{p.purpose}</span>
              </div>
            ))}
          </div>
          <p className="mt-4">
            For transfers outside the UK/EEA, we rely on Standard Contractual Clauses (SCCs) or equivalent transfer mechanisms where applicable.
          </p>
        </Section>

        <Section title="5. Data Retention">
          <p>We retain data only as long as necessary:</p>
          <ul className="space-y-2 mt-2">
            {[
              "Generated content and usage data: retained while the service is active or for up to 24 months of inactivity.",
              "Payment records: retained for 7 years in accordance with UK financial regulations.",
              "Anonymous identifiers: retained indefinitely unless deletion is requested.",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF0000] mt-2 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="6. Your Rights Under UK GDPR">
          <p>You have the right to access, correct, delete, or port your data, and to object to or restrict its processing. To exercise any right, contact us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF0000] hover:underline">{CONTACT_EMAIL}</a>.
            We will respond within 30 days.
          </p>
          <p>
            You also have the right to lodge a complaint with the UK Information Commissioner's Office (ICO) at{" "}
            <span className="text-white">ico.org.uk</span>.
          </p>
        </Section>

        <Section title="7. Data Breach Procedure">
          <p>
            In the event of a personal data breach, {COMPANY} will notify the ICO within 72 hours where the breach is likely to result in a risk to individuals' rights and freedoms. Affected users will be notified without undue delay where the breach is likely to result in a high risk to their rights.
          </p>
        </Section>

        <Section title="8. Contact">
          <p>
            For any data or compliance-related queries, contact {COMPANY} at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF0000] hover:underline">{CONTACT_EMAIL}</a>.
          </p>
        </Section>
      </main>

      <footer className="py-8 text-center border-t border-[#1A1A1A]">
        <p className="text-gray-700 text-xs">
          &copy; {new Date().getFullYear()}{" "}
          <span className="text-gray-500 font-semibold">{COMPANY}</span> · All rights reserved
        </p>
      </footer>
    </div>
  );
}
